import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDownUp, Flame, Megaphone } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { getLocalizedText } from '../components/MultiLanguageInput';
import { priceToRarity } from '../components/landing';
import { PackBanner, ChaseCard, usd, BannerSlider, CategorySection } from '../components/marketplace';
import packsHero from '../assets/cardtopia-packs.webp';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Home = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const [pools, setPools] = useState([]);
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [sortDesc, setSortDesc] = useState(true);

  const NEWS = [
    '🎉 Welcome to Cardtopia — Open Packs, Chase Legends!',
    '🔥 New Origins Booster Packs are now live. Rip yours today!',
    '💎 Pull rare NFT-graded cards and add them to your collection.',
    '🎫 Earn Gacha Tickets on every pull — redeem them at the TCG Shop.',
    '⚡ Instant deposits with SOL. Top up and start ripping in seconds.',
    '🏆 Chase THE ONE AND ONLY cards — only one exists per pool!',
  ];
  const [newsIdx, setNewsIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNewsIdx((i) => (i + 1) % NEWS.length), 4500);
    return () => clearInterval(id);
  }, [NEWS.length]);


  useEffect(() => {
    axios.get(`${API}/pools`).then((r) => setPools(r.data)).catch(() => {});
    axios.get(`${API}/banners`).then((r) => setBanners(r.data || [])).catch(() => {});
    axios.get(`${API}/categories`).then((r) => setCategories(r.data || [])).catch(() => {});
    axios.get(`${API}/subcategories`).then((r) => setSubcategories(r.data || [])).catch(() => {});
  }, []);

  const goPool = (id) => navigate(`/pool/${id}`);

  const ripBanners = useMemo(
    () => (banners || []).filter((b) => b.is_active).sort((a, b) => (a.order || 0) - (b.order || 0)),
    [banners]
  );

  const handleBannerClick = (b) => {
    if (!b) { navigate('/pools'); return; }
    if (b.link_type === 'pool' && b.link_id) navigate(`/pool/${b.link_id}`);
    else if (b.link_url && b.link_url !== '#') window.open(b.link_url, '_blank');
    else navigate('/pools');
  };

  const packImgs = useMemo(() => pools.slice(0, 4).map((p) => p.image_url).filter(Boolean), [pools]);

  const sections = useMemo(() => {
    const subMap = Object.fromEntries(subcategories.map((s) => [s.id, s]));
    const catMeta = Object.fromEntries(categories.map((c) => [c.id, c]));
    const byCat = {};
    pools.forEach((p) => {
      const cid = p.category_id || 'other';
      if (!byCat[cid]) {
        const meta = catMeta[cid];
        byCat[cid] = {
          id: cid,
          name: getLocalizedText(meta?.name || p.category_name, lang) || 'Collection',
          icon: meta?.icon_url || null,
          order: meta?.sort_order != null ? meta.sort_order : 999,
          pools: [],
        };
      }
      byCat[cid].pools.push(p);
    });
    const arr = Object.values(byCat).map((sec) => {
      const bySub = {};
      const noSub = [];
      sec.pools.forEach((p) => {
        const sid = p.subcategory_id;
        if (sid && subMap[sid]) {
          if (!bySub[sid]) bySub[sid] = { sub: subMap[sid], pools: [] };
          bySub[sid].pools.push(p);
        } else {
          noSub.push(p);
        }
      });
      return { ...sec, total: sec.pools.length, subGroups: Object.values(bySub), noSub };
    });
    arr.sort((a, b) => a.order - b.order || b.total - a.total);
    return arr;
  }, [pools, subcategories, categories, lang]);

  return (
    <>
      <SEOHead page="home" />

      <div className="max-w-[1500px] mx-auto px-4 lg:px-8 pt-6 lg:pt-8 pb-3 lg:pb-4">
        {/* Pack / Rips banners */}
        <div className="grid sm:grid-cols-2 gap-4 lg:gap-6">
          <PackBanner label="Packs" sub="Open sealed packs and pull rare cards." heroImage={packsHero} accent="#FFD400" onClick={() => navigate('/pools')} testid="banner-packs" />
          <BannerSlider banners={ripBanners} onNavigate={handleBannerClick} testid="banner-rips" />
        </div>

        {/* Live stats strip */}
        <div id="live-stats" className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 scroll-mt-20">
          {[
            { k: 'Live Packs', v: pools.length },
            { k: 'Total Pulls', v: pools.reduce((s, p) => s + Math.max(0, (p.total_quantity || 0) - (p.remaining_quantity || 0)), 0).toLocaleString() },
            { k: 'Top Card', v: usd(Math.max(0, ...pools.map((p) => p.price || 0))) },
            { k: 'Players', v: '12.4K' },
          ].map((s, i) => (
            <div key={i} className="bg-white/30 backdrop-blur-xl border border-white/60 rounded-2xl px-4 py-3 shadow-[0_10px_28px_-16px_rgba(0,0,0,0.28)] ring-1 ring-inset ring-white/40">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#8A7A55]">{s.k}</p>
              <p className="text-xl font-black text-[#1A1206] font-['Unbounded'] mt-0.5">{s.v}</p>
            </div>
          ))}
        </div>

        {/* News ticker + Sort control */}
        <div className="mt-8 flex items-center justify-between gap-3">
          {/* Rotating platform news (1 line) */}
          <div className="flex items-center gap-2 min-w-0 flex-1 bg-white/30 backdrop-blur-xl border border-white/60 ring-1 ring-inset ring-white/40 rounded-full pl-3 pr-4 h-9 shadow-[0_10px_28px_-16px_rgba(0,0,0,0.28)] overflow-hidden" data-testid="news-ticker">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-[#FFD400] to-[#FF8C00] text-[#1A1206] shrink-0">
              <Megaphone size={13} />
            </span>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#FF6A00] shrink-0 hidden sm:inline">News</span>
            <div className="min-w-0 flex-1 relative h-9">
              {NEWS.map((n, i) => (
                <span
                  key={i}
                  className="absolute inset-0 flex items-center text-sm font-semibold text-[#1A1206] truncate transition-all duration-500"
                  style={{ opacity: i === newsIdx ? 1 : 0, transform: i === newsIdx ? 'translateY(0)' : 'translateY(6px)', pointerEvents: 'none' }}
                >
                  {n}
                </span>
              ))}
            </div>
          </div>

          {/* Sort */}
          <button onClick={() => setSortDesc((s) => !s)} data-testid="sort-toggle" className="shrink-0 inline-flex items-center gap-2 bg-white border border-[#F0E3C8] rounded-full pl-3 pr-2 h-9 text-sm font-bold text-[#1A1206] hover:border-[#FF8C00] hover:shadow-[0_6px_16px_-8px_rgba(255,140,0,0.5)] transition-all">
            <ArrowDownUp size={14} className="text-[#FF8C00]" />
            <span>Sort</span>
            <span className="inline-flex items-center justify-center min-w-[38px] h-6 px-2 rounded-full bg-gradient-to-r from-[#FFD400] to-[#FF8C00] text-[#1A1206] text-xs font-extrabold">
              {sortDesc ? 'High' : 'Low'}
            </span>
          </button>
        </div>
      </div>

      {/* Category sections — full-width amber band per category */}
      {sections.length > 0 ? (
        sections.map((sec) => (
          <CategorySection key={sec.id} section={sec} sortDesc={sortDesc} lang={lang} onPool={goPool} />
        ))
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-state">
          <Flame size={44} className="text-[#7A5C00] mb-3" />
          <p className="text-[#7A5C00]">Loading top chase cards…</p>
        </div>
      )}
      <div className="h-10" />
    </>
  );
};
